/**
 * The `havens` section — **Safe-haven correlations** (owner, 2026-11-02; spec
 * `docs/mockups/macro-v2.html` § 04, where it sat under What moves together as
 * "Hedges & havens"): stocks vs bonds, bonds vs gold, bonds vs dollar, each with
 * its warning side tinted and a plain-language read.
 *
 * Its own kind so it can sit beside `positioning` in the stacked Macro layout.
 * It reads the same nightly derive as `relationships`, through the one fetch
 * helper (`relationships-shared.tsx`). Where the derive returned null for a
 * window, the gauge says `NO_HISTORY` — nothing is struck client-side.
 *
 * The **warning line** (0 · ±0.2 · ±0.3) is the section's config, `line`;
 * absent means the server's own default (`hedgeWarn`). Admin only —
 * `MacroSectionHost` refuses anyone else before this mounts. Every chart
 * measures its width (`useWidth`), so at half width it fills its half.
 */

import type { CSSProperties } from "react";
import type { RelationshipsPayload } from "../../api/macro-relationships";
import {
  HEDGE_LINES,
  HEDGES,
  hedgeWarns,
  lastValue,
  NO_HISTORY,
  signed2,
  warnRuns,
  yearBreaks,
  type HedgeDef,
} from "../../lib/macro-relationships";
import { macroScale } from "../../lib/macro-sections";
import { useTokenResolver } from "../theme";
import { useWidth } from "./use-width";
import type { MacroSectionProps } from "./types";
import { pathOf, S, Seg, useRelationshipsPayload } from "./relationships-shared";

export default function HavensSection({ section, config, onConfig }: MacroSectionProps<"havens">) {
  const scale = macroScale(config);
  const load = useRelationshipsPayload();
  const data = load.state === "ready" ? load.data : null;
  // The saved line, else the server's own default.
  const line = config.line ?? data?.hedgeWarn ?? 0;

  return (
    <div data-macro-kind="havens" style={S.root}>
      <div style={S.head}>
        <strong style={S.title}>{section.title ?? "Safe-haven correlations"}</strong>
        <Seg
          label="Warning line"
          value={line}
          options={HEDGE_LINES.map((v) => ({ v, text: v === 0 ? "0" : `±${v.toFixed(1)}` }))}
          onPick={(v) => onConfig?.({ ...config, line: v as 0 | 0.2 | 0.3 })}
        />
        <span style={{ flex: 1 }} />
        <span style={S.dim}>weekly changes{data?.asOf ? ` · as of ${data.asOf}` : ""}</span>
      </div>
      {load.state === "loading" && <div style={S.note}>Loading safe-haven correlations…</div>}
      {load.state === "error" && (
        <div role="alert" style={S.note}>
          {load.message}
        </div>
      )}
      {data && (
        <div style={S.body}>
          <div style={S.box}>
            <Hedges data={data} line={line} scale={scale} />
          </div>
        </div>
      )}
    </div>
  );
}

// -- the gauges ------------------------------------------------------------------

function Hedges({ data, line, scale }: { data: RelationshipsPayload; line: number; scale: number }) {
  return (
    <div>
      {HEDGES.map((def, gi) => {
        const g = data.hedges.find((h) => h.a === def.a && h.b === def.b);
        return <Gauge key={def.name} def={def} gauge={g} line={line} last={gi === HEDGES.length - 1} scale={scale} />;
      })}
      <Legend />
    </div>
  );
}

function Gauge({
  def, gauge, line, last, scale,
}: {
  def: HedgeDef;
  gauge: RelationshipsPayload["hedges"][number] | undefined;
  line: number;
  last: boolean;
  scale: number;
}) {
  const tok = useTokenResolver();
  const s13 = gauge?.r13Series ?? [];
  const s52 = gauge?.r52Series ?? [];
  const weeks = gauge?.week ?? [];
  // The derive's newest r per window, as sent: a null is too little history, and
  // is said so — never filled from an older point of the series.
  const today13 = gauge?.r13 ?? null;
  const today52 = gauge?.r52 ?? null;
  const w = hedgeWarns(def.side, today13, line);
  // The viewBox is the measured width, so the x-scale stretches and text stays 1:1.
  const [svgRef, W] = useWidth(1000);
  // Taller gauges (owner, 2026-11-01).
  const H = Math.round(180 * scale) + (last ? 16 : 0), L = 44, R = W - 12, T = 8, B = H - (last ? 22 : 8);
  const len = Math.max(s52.length, s13.length);
  const X = (i: number) => L + (i * (R - L)) / Math.max(1, len - 1);
  const Y = (v: number) => T + ((1 - v) / 2) * (B - T);
  const th = def.side * line;
  const zy1 = def.side > 0 ? Y(1) : Y(th), zy2 = def.side > 0 ? Y(th) : Y(-1);
  const runs = warnRuns(s52, def.side, line);
  const dot = today13 == null ? null : lastValue(s13);
  const state = w == null ? NO_HISTORY : w ? def.badWord : def.okWord;
  const missing = [today13 == null && "13w", today52 == null && "52w"].filter(Boolean).join(" and ");
  return (
    <div
      data-hedge={`${def.a}|${def.b}`}
      data-warn={w == null ? undefined : String(w)}
      data-no-history={missing || undefined}
    >
      <div style={S.hhh}>
        <span>
          <b>{def.name}</b>{" "}
          <span style={S.dim}>
            ({def.a} × {def.b}) · {def.question}
          </span>
        </span>
        <span style={{ flex: 1 }} />
        <span style={S.mono}>
          13w <b style={{ color: w == null ? "var(--qf-text-muted)" : w ? "var(--qf-negative)" : "var(--qf-positive)" }}>{signed2(today13)}</b> · 52w{" "}
          <b>{signed2(today52)}</b>
        </span>
        <span style={w == null ? S.pillNone : w ? S.pillBad : S.pillOk}>{state}</span>
      </div>
      {w != null && (
        <div style={{ ...S.read, color: w ? "var(--qf-negative)" : "var(--qf-positive)" }}>
          {w ? `⚠ ${def.bad}` : `✓ ${def.ok}`}
        </div>
      )}
      {missing && (
        <div style={{ ...S.read, color: "var(--qf-text-muted)" }}>
          {missing}: {NO_HISTORY}
        </div>
      )}
      {len < 2 ? (
        <div style={S.note}>No history for this pair yet.</div>
      ) : (
        <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: "block" }} aria-label={`${def.name} history`} role="img">
          <rect x={L} y={zy1} width={R - L} height={Math.max(0, zy2 - zy1)} fill={tok("negative")} fillOpacity={0.06} />
          {runs.map(([i0, i1]) => (
            <path
              key={i0}
              d={
                `M${X(i0)},${Y(th)}` +
                s52.slice(i0, i1 + 1).map((v, k) => `L${X(i0 + k)},${Y(v ?? th)}`).join("") +
                `L${X(i1)},${Y(th)}Z`
              }
              fill={tok("negative")}
              fillOpacity={0.28}
            />
          ))}
          {[1, 0, -1].map((v) => (
            <g key={v}>
              <line x1={L} x2={R} y1={Y(v)} y2={Y(v)} stroke={tok(v ? "grid" : "axis")} />
              <text x={L - 6} y={Y(v) + 3} textAnchor="end" style={{ fontSize: 10.5, fill: tok("text.muted") }}>
                {v > 0 ? "+1" : v < 0 ? "−1" : "0"}
              </text>
            </g>
          ))}
          {line !== 0 && (
            <line x1={L} x2={R} y1={Y(th)} y2={Y(th)} stroke={tok("negative")} strokeDasharray="4 3" strokeOpacity={0.7} />
          )}
          {yearBreaks(weeks).map(({ i, year }) => (
            <g key={year}>
              <line x1={X(i)} x2={X(i)} y1={T} y2={B} stroke={tok("grid")} />
              {last && (
                <text x={X(i) + 3} y={H - 5} style={{ fontSize: 10.5, fill: tok("text.muted") }}>{year}</text>
              )}
            </g>
          ))}
          <path d={pathOf(s13, X, Y)} fill="none" stroke={tok("text.muted")} strokeWidth={1} />
          <path d={pathOf(s52, X, Y)} fill="none" stroke={tok("accent")} strokeWidth={3} />
          {dot && <circle cx={X(dot.i)} cy={Y(dot.v)} r={4} fill={tok(w ? "negative" : "positive")} />}
        </svg>
      )}
    </div>
  );
}

function Legend() {
  const sw = (bg: string): CSSProperties => ({ display: "inline-block", width: 10, height: 3, background: bg, marginRight: 5, verticalAlign: "middle" });
  return (
    <div style={{ ...S.dim, display: "flex", flexWrap: "wrap", gap: 14, padding: "6px 12px 10px" }}>
      <span><i style={sw("var(--qf-accent)")} />52w</span>
      <span><i style={sw("var(--qf-text-muted)")} />13w</span>
      <span><i style={{ ...sw("var(--qf-negative)"), opacity: 0.6 }} />warning zone · dark red = 52w inside it</span>
      <span>● today, coloured by the 13w state</span>
    </div>
  );
}
